import { useQuery } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { CounterpartiesStats, FlowWindow } from "../api/types";

/**
 * Такт опроса свода встреч.
 *
 * Тридцать секунд, как у истории сделок, и по той же причине: источник — НАШ
 * журнал, он пополняется нашими же исполнениями, и филл, приехавший по сокету,
 * оператор увидит в ленте мгновенно. Вкладка отвечает на вопрос о прошлом
 * («кто берёт у меня каждый день»), и секунда запаздывания в ней ничего не меняет.
 */
const POLL_MS = 30_000;

export function counterpartiesKey(params: {
  sinceHours: number;
  market: string | undefined;
  flow: FlowWindow | undefined;
}) {
  return [
    "counterparties",
    params.sinceHours,
    params.market ?? "",
    params.flow ?? "",
  ] as const;
}

/**
 * Свод встреч с контрагентами: кто принял наш налив и кто снял нас.
 *
 * Опрос REST, а не сокет: это СОСТОЯНИЕ («кто сколько у меня взял»), а не поток,
 * где важен порядок, — то же правило, что у панели накопления и счётчика ленты.
 *
 * Окно статистики коротких рынков входит в ключ: своды за сутки и за месяц —
 * разные числа, и общий ключ показал бы месячные под подписью «сегодня».
 */
export function useCounterparties(params: {
  sinceHours: number;
  market: string | undefined;
  flow: FlowWindow | undefined;
}) {
  return useQuery<CounterpartiesStats>({
    queryKey: counterpartiesKey(params),
    queryFn: () =>
      api
        .counterparties({
          sinceHours: params.sinceHours,
          market: params.market,
          flow: params.flow,
        })
        .then((r) => r.counterparties),
    // Прошлый свод остаётся на экране, пока едет новый, — но ТОЛЬКО пока рынок
    // тот же. Переключение периода не должно мигать пустой таблицей; смена
    // рынка-фильтра — обязана, иначе под новым рынком секунду стоят встречи из
    // прошлого. Тот же довод и то же правило, что у панели накопления.
    placeholderData: (prev, prevQuery) =>
      prevQuery?.queryKey[2] === (params.market ?? "") ? prev : undefined,
    refetchInterval: POLL_MS,
    // В фоновой вкладке не опрашиваем: таблицу читают глазами.
    refetchIntervalInBackground: false,
  });
}
